/**
 * One animal's profile, for members. Same layout as the public profile
 * page, with more in the timeline: a members-only event carries the same
 * public_caption a stranger would never see, marked with a quiet "members
 * only" tag so it's clear what the share is buying, never the clinical
 * note it was written from (services/api/src/livestock-public.ts holds
 * that line, not this component).
 */

import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { JSX } from 'preact';

import { apiGet, ApiError } from '../api.js';
import { humanDay } from './LogView.js';

interface Event {
  id: string;
  event_type: string;
  occurred_at: string;
  caption: string | null;
  members_only: boolean;
}

interface MediaItem {
  id: string;
  url: string | null;
  content_type: string;
  alt_text: string | null;
  caption: string | null;
}

interface Animal {
  name: string | null;
  species: string;
  blurb: string | null;
  photo_url: string | null;
}

interface Profile {
  animal: Animal;
  events: Event[];
  media: MediaItem[];
}

function Media({ item }: { item: MediaItem }): JSX.Element | null {
  if (!item.url) return null;
  return item.content_type.startsWith('video/') ? (
    <video src={item.url} controls playsInline preload="metadata" />
  ) : (
    <img src={item.url} alt={item.alt_text ?? ''} loading="lazy" />
  );
}

export function FlockProfileView({ slug }: { slug: string }): JSX.Element {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<Profile>(`/member/v1/flock/${encodeURIComponent(slug)}`)
      .then(setProfile)
      .catch((err) =>
        setError(err instanceof ApiError && err.status === 404 ? 'That profile is not here.' : 'Could not load it.'),
      );
  }, [slug]);

  if (error) {
    return (
      <main class="view centred">
        <p class="error" role="alert">{error}</p>
        <p>
          <Link href="/flock">← The flock</Link>
        </p>
      </main>
    );
  }

  if (!profile) {
    return (
      <main class="view centred">
        <p class="muted" role="status">Loading…</p>
      </main>
    );
  }

  const { animal, events, media } = profile;
  const name = animal.name ?? 'Unnamed';

  return (
    <main class="view profile-view">
      <Link href="/flock" class="link">← The flock</Link>
      <header class="profile-intro">
      {animal.photo_url && <img class="entry-hero" src={animal.photo_url} alt={name} />}

      <div><p class="ff-eyebrow">{animal.species.charAt(0) + animal.species.slice(1).toLowerCase()}</p>
      <h1>{name}</h1>
      {animal.blurb && <p>{animal.blurb}</p>}
      </div></header>

      {events.length > 0 && (
        <section><h2>Life on the farm</h2><div class="stack timeline">
          {events.map((event) => (
            <div key={event.id} class="card">
              <span class="card-sub">
                {humanDay(event.occurred_at)}
                {event.members_only && (
                  <span class="tag" style="margin-left: 0.5rem;">
                    Members only
                  </span>
                )}
              </span>
              {event.caption && <p style="margin: 0.4rem 0 0;">{event.caption}</p>}
            </div>
          ))}
        </div></section>
      )}

      {media.length > 0 && (
        <section><h2>In pictures</h2><div class="photo-grid" style="margin-top: 1.5rem;">
          {media.map((item) => (
            <Media key={item.id} item={item} />
          ))}
        </div></section>
      )}

      <p style="margin-top: 2rem;">
        <Link href="/flock">← The flock</Link>
      </p>
    </main>
  );
}
